import api from 'api';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { Tooltip } from '@mui/material';
import { LTHeader, LTItem, LTPlaceholder, LTMobileItem } from 'components/common/ListTable';
import Checkbox from 'components/common/Checkbox/Checkbox';
import { CustomLTRow } from 'views/Dashboards-B2B/components/customLTRow/customLTRow';
import { Ticket } from 'api/models/response/Ticket';
import { TicketStatus } from 'api/models/payload/Ticket';
import * as S from '../../../Dropdowns.styled';
import { useTranslation } from 'react-i18next';

type TicketsDropdownProps = {
    open: boolean;
    handleOpenCloseTicketsDropdown: () => void;
    mobile: boolean;
};

const TicketsDropdown = ({
    open,
    handleOpenCloseTicketsDropdown,
    mobile,
}: TicketsDropdownProps) => {
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const [tickets, setTickets] = useState<Ticket[]>([]);

    const fetchTickets = async (appID: string) => {
        try {
            const response = await api.tickets.getTicketsByApplication(appID);

            setTickets(response.data);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (activeApp && open) {
            fetchTickets(activeApp.id.toString());
        }
    }, [activeApp, open]);

    const handleChangeItem = async (id: number, checkedStatus: boolean) => {
        const target = tickets.find((ticket) => ticket.id === id);

        if (target) {
            const payload = {
                id: target.id,
                status: checkedStatus ? TicketStatus.RESOLVED : TicketStatus.PENDING,
            };

            try {
                await api.tickets.editTicket(payload);
                if (activeApp) {
                    fetchTickets(activeApp.id.toString());
                }
            } catch (e) {
                console.error(e);
            }
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleString(language === 'EN' ? 'default' : 'fr', { month: 'short' });
        return `${day} ${month}`;
    };

    const { t } = useTranslation();

    return (
        <>
            <S.Dropdown onClick={() => handleOpenCloseTicketsDropdown()}>
                {t('tickets', { lng: language })}
                <S.DropdownIcon style={{ width: '17px', transform: open ? 'scaleY(-1)' : '' }} />
            </S.Dropdown>

            {activeApp && (
                <S.TicketsContainer mobile={mobile} open={open}>
                    <S.ListTable>
                        <LTHeader>
                            {/* tooltips are present in LTItem itself */}
                            {/* <LTItem maxWidth="12%" minWidth="none" style={{ textAlign: 'center' }}>
                                ID
                            </LTItem> */}
                            <LTItem
                                maxWidth="calc((95% - 87px) / 2)"
                                minWidth="calc((95% - 87px) / 2)"
                            >
                                {t('document', { lng: language })}
                            </LTItem>
                            <LTItem
                                maxWidth="calc((95% - 87px) / 2)"
                                minWidth="calc((95% - 87px) / 2)"
                            >
                                {t('description', { lng: language })}
                            </LTItem>
                            <LTItem maxWidth="52px" minWidth="52px" style={{ textAlign: 'center' }}>
                                {t('created', { lng: language })}
                            </LTItem>
                            <LTItem maxWidth="35px" minWidth="35px" style={{ textAlign: 'center' }}>
                                {t('done', { lng: language })}
                            </LTItem>
                        </LTHeader>
                        {tickets.length === 0 && (
                            <LTPlaceholder> {t('theListIsEmpty', { lng: language })}</LTPlaceholder>
                        )}

                        <>
                            {tickets.length > 0 &&
                                tickets.map((ticket) => (
                                    <CustomLTRow key={ticket.id} id={ticket.id.toString()}>
                                        {/*FULL SCREEN VERSION*/}
                                        {/* <LTItem
                                            maxWidth="12%"
                                            minWidth="none"
                                            style={{ textAlign: 'center' }}>
                                            {ticket.id}
                                        </LTItem> */}
                                        <LTItem
                                            maxWidth="calc((95% - 87px) / 2)"
                                            minWidth="calc((95% - 87px) / 2)"
                                        >
                                            {ticket.document_name}
                                        </LTItem>

                                        <LTItem
                                            maxWidth="calc((95% - 87px) / 2)"
                                            minWidth="calc((95% - 87px) / 2)"
                                        >
                                            {ticket.text}
                                        </LTItem>
                                        <LTItem
                                            maxWidth="52px"
                                            minWidth="52px"
                                            style={{ textAlign: 'center' }}
                                        >
                                            {formatDate(ticket.created.toString())}
                                        </LTItem>
                                        <LTItem maxWidth="35px" minWidth="35px">
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: '0.5rem',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Tooltip title="Mark Done">
                                                    <div className="this div makes the tooltip work">
                                                        <Checkbox
                                                            checked={
                                                                ticket.status ===
                                                                TicketStatus.RESOLVED
                                                            }
                                                            onChange={(__, checked) =>
                                                                handleChangeItem(ticket.id, checked)
                                                            }
                                                        />
                                                    </div>
                                                </Tooltip>
                                            </div>
                                        </LTItem>
                                        {/*SMALL SCREEN VERSION*/}
                                        {/* <LTMobileItem name="ID">{ticket.id}</LTMobileItem> */}
                                        <LTMobileItem name="Document">
                                            {ticket.document_name}
                                        </LTMobileItem>
                                        <LTMobileItem name="Description">
                                            {ticket.text}
                                        </LTMobileItem>
                                        <LTMobileItem name="Created">
                                            {formatDate(ticket.created.toString())}
                                        </LTMobileItem>
                                        <LTMobileItem name="Done">
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <Checkbox
                                                    checked={
                                                        ticket.status === TicketStatus.RESOLVED
                                                    }
                                                    onChange={(__, checked) =>
                                                        handleChangeItem(ticket.id, checked)
                                                    }
                                                />
                                            </div>
                                        </LTMobileItem>
                                    </CustomLTRow>
                                ))}
                        </>
                    </S.ListTable>
                </S.TicketsContainer>
            )}
        </>
    );
};

export default TicketsDropdown;
